import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import html2canvas from "html2canvas";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { decryptAppId, decryptText } from "../../utils/encryption";

const ILACMarkDownload = () => {
  const captureRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [certNo, setCertNo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const encryptedAppId = searchParams.get("appId");
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;

  useEffect(() => {
    if (!appId) {
      setError("Invalid application ID.");
      setLoading(false);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_URL}/ilac/certificate/${appId}`)
      .then((res) => {
        if (res.data.success && res.data.data) {
          setCertNo(res.data.data.Cert_No || "");
        }
      })
      .catch(() => setError("Failed to load certificate details."))
      .finally(() => setLoading(false));
  }, [appId]);

  const handleDownload = async () => {
    if (!captureRef.current) return;
    const canvas = await html2canvas(captureRef.current, { useCORS: true, scale: 2 });
    const image = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = `${certNo || "ILAC-MRA"}_Ilac-MRA-MONO.png`;
    link.click();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        backgroundColor: "#f5f7fa",
        py: 4,
        mt: 6,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 700, width: "100%", borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom align="center">
          Download NABL Accredited CAB Combined ILAC MRA Mark
        </Typography>

        <Box textAlign="center" mb={3}>
          <Typography variant="body2" color="text.secondary">
            Note: Please read how to use NABL Accredited CAB Combined ILAC MRA Mark before downloading.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => window.open("/main/images/NABL-133-doc.pdf", "_blank")}
            sx={{ mt: 1 }}
          >
            Click here to read instructions
          </Button>
        </Box>

        {/* Capture area */}
        <Box
          ref={captureRef}
          sx={{
            background: "#fff",
            textAlign: "center",
            px: 2,
            py: 2,
            mb: 3,
          }}
        >
          <img
            src="/main/images/ILAC_NABL_Logo.png"
            alt="NABL Accredited CAB Combined ILAC MRA Mark"
            style={{ width: "70%", maxWidth: 480 }}
            crossOrigin="anonymous"
          />
          {certNo && (
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "24px",
                mt: 1,
              }}
            >
              {certNo}
            </Typography>
          )}
        </Box>

        <Box textAlign="center">
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              background: "linear-gradient(135deg, #00024c 0%, #1a1aff 100%)",
              color: "#fff",
              fontWeight: 600,
              px: 4,
              "&:hover": {
                background: "linear-gradient(135deg, #00013a 0%, #0000cc 100%)",
              },
            }}
          >
            Download
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ILACMarkDownload;